// Event categories — must match backend `EventCategories.All` (Phase 9). Grouped,
// searchable, multi-select. Events can belong to several; the FIRST selected is the
// "primary" (drives the Featured "1 per category per region" rule). `other` is the
// fallback + allows a custom label; it never claims a Featured slot.

export const EVENT_CATEGORIES = [
  // Music & Nightlife
  { slug: 'concerts', label: 'Concerts', group: 'Music & Nightlife' },
  { slug: 'music', label: 'Music', group: 'Music & Nightlife' },
  { slug: 'live-music', label: 'Live Music', group: 'Music & Nightlife' },
  { slug: 'dj', label: 'DJ / Electronic', group: 'Music & Nightlife' },
  { slug: 'nightlife', label: 'Nightlife', group: 'Music & Nightlife' },
  { slug: 'karaoke', label: 'Karaoke', group: 'Music & Nightlife' },
  { slug: 'dance', label: 'Dance', group: 'Music & Nightlife' },
  // Food & Drink
  { slug: 'food-drink', label: 'Food & Drink', group: 'Food & Drink' },
  { slug: 'dining', label: 'Dining', group: 'Food & Drink' },
  { slug: 'wine', label: 'Wine', group: 'Food & Drink' },
  { slug: 'beer', label: 'Beer & Breweries', group: 'Food & Drink' },
  { slug: 'cocktails', label: 'Cocktails & Spirits', group: 'Food & Drink' },
  { slug: 'coffee', label: 'Coffee & Tea', group: 'Food & Drink' },
  // Arts & Culture
  { slug: 'art', label: 'Art', group: 'Arts & Culture' },
  { slug: 'arts-culture', label: 'Arts & Culture', group: 'Arts & Culture' },
  { slug: 'performing-arts', label: 'Performing Arts', group: 'Arts & Culture' },
  { slug: 'theatre', label: 'Theatre', group: 'Arts & Culture' },
  { slug: 'film', label: 'Film & Media', group: 'Arts & Culture' },
  { slug: 'visual-arts', label: 'Visual Arts', group: 'Arts & Culture' },
  { slug: 'literary', label: 'Literary & Books', group: 'Arts & Culture' },
  // Comedy & Games
  { slug: 'comedy', label: 'Comedy', group: 'Comedy & Games' },
  { slug: 'trivia', label: 'Trivia', group: 'Comedy & Games' },
  { slug: 'games', label: 'Games & Board Games', group: 'Comedy & Games' },
  { slug: 'improv', label: 'Improv', group: 'Comedy & Games' },
  // Sports & Outdoors
  { slug: 'sports-fitness', label: 'Sports & Fitness', group: 'Sports & Outdoors' },
  { slug: 'yoga', label: 'Yoga & Fitness', group: 'Sports & Outdoors' },
  { slug: 'running-cycling', label: 'Running & Cycling', group: 'Sports & Outdoors' },
  { slug: 'outdoors', label: 'Outdoors & Hiking', group: 'Sports & Outdoors' },
  { slug: 'adventure', label: 'Adventure', group: 'Sports & Outdoors' },
  { slug: 'water-sports', label: 'Water Sports', group: 'Sports & Outdoors' },
  { slug: 'winter-sports', label: 'Winter Sports', group: 'Sports & Outdoors' },
  // Community & Learning
  { slug: 'community', label: 'Community', group: 'Community & Learning' },
  { slug: 'networking', label: 'Networking', group: 'Community & Learning' },
  { slug: 'business', label: 'Business & Professional', group: 'Community & Learning' },
  { slug: 'tech', label: 'Tech', group: 'Community & Learning' },
  { slug: 'education', label: 'Education & Workshops', group: 'Community & Learning' },
  { slug: 'language', label: 'Language & Culture', group: 'Community & Learning' },
  // Lifestyle
  { slug: 'wellness', label: 'Wellness', group: 'Lifestyle' },
  { slug: 'family', label: 'Family & Kids', group: 'Lifestyle' },
  { slug: 'fashion-beauty', label: 'Fashion & Beauty', group: 'Lifestyle' },
  { slug: 'home-lifestyle', label: 'Home & Lifestyle', group: 'Lifestyle' },
  { slug: 'hobbies', label: 'Hobbies & Crafts', group: 'Lifestyle' },
  { slug: 'pets', label: 'Pets & Animals', group: 'Lifestyle' },
  { slug: 'travel', label: 'Travel', group: 'Lifestyle' },
  // Cause & Spirit
  { slug: 'charity', label: 'Charity & Causes', group: 'Cause & Spirit' },
  { slug: 'spirituality', label: 'Religion & Spirituality', group: 'Cause & Spirit' },
  { slug: 'lgbtq', label: 'LGBTQ+', group: 'Cause & Spirit' },
  // Seasonal
  { slug: 'holiday-seasonal', label: 'Holiday & Seasonal', group: 'Seasonal' },
  { slug: 'festivals', label: 'Festivals & Fairs', group: 'Seasonal' },
  // Other (custom)
  { slug: 'other', label: 'Other', group: 'Other' },
];

// The Simple view's quick-pick chips (handoff "Event creator — Simple / Advanced"), in this
// order. Labels come from EVENT_CATEGORIES; a slug missing there is dropped, never invented.
export const QUICK_CATEGORY_SLUGS = ['nightlife', 'live-music', 'food-drink', 'wine', 'arts-culture', 'comedy', 'sports-fitness', 'outdoors', 'community', 'wellness', 'family'];
export const QUICK_CATEGORIES = QUICK_CATEGORY_SLUGS
  .map((slug) => EVENT_CATEGORIES.find((c) => c.slug === slug))
  .filter(Boolean);

// Max categories a single event can carry (keeps cards + filters legible).
export const MAX_EVENT_CATEGORIES = 3;

// Every category can be featured, Other included. It used to be excluded, but
// ingest files ~45% of live events there, so the rule blocked most of the
// catalogue rather than just the junk. Mirrors EventCategories.Featurable.
export const FEATURABLE_CATEGORIES = EVENT_CATEGORIES;

// Ordered unique group names, for a grouped picker.
export const CATEGORY_GROUPS = EVENT_CATEGORIES.reduce((acc, c) => {
  if (!acc.includes(c.group)) acc.push(c.group);
  return acc;
}, []);

export function categoryLabel(slug) {
  return EVENT_CATEGORIES.find((c) => c.slug === slug)?.label || 'Other';
}

// Parse the backend's comma-separated categories string into an array of slugs.
export function parseCategories(csv, fallback = 'other') {
  if (!csv || typeof csv !== 'string') return fallback ? [fallback] : [];
  const list = csv.split(',').map((s) => s.trim()).filter(Boolean);
  return list.length ? list : (fallback ? [fallback] : []);
}
